import { api } from './api';

const normalizeAssignment = (assignment = {}) => {
  const studentProgress = assignment.student_progress || {};
  const attemptCount = Number(studentProgress.attempt_count || 0);
  const resultAttemptId = studentProgress.last_attempt_id || null;

  return {
    ...assignment,
    isCompleted: attemptCount > 0,
    resultAttemptId
  };
};

/**
 * Test Service for handling dynamic test fetching in ContinueCourse
 * Separates test-related API calls and logic from the main component
 */

export const testService = {

  /**
   * Fetch tests for a specific module (Final Exams)
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @param {string} moduleId - Module ID
   * @returns {Promise<Array>} Array of test assignments
   */
  fetchModuleTests: async (userId, courseId, moduleId) => {
    try {
      const response = await api.getStudentAssignmentsDirect(userId, { 
        exam_type: 'Final Exam', 
        course_id: courseId,
        module_id: moduleId
      });

      // Flatten and filter assignments for this module
      const assignments = [];
      if (response.data.lessons) {
        response.data.lessons.forEach(lesson => {
          if (lesson.course_id === parseInt(courseId) && lesson.module_id === parseInt(moduleId)) {
                if (lesson.assignments_by_level) {
                  Object.values(lesson.assignments_by_level).forEach(levelAssignments => {
                    levelAssignments.forEach(assignment => {
                      assignments.push(normalizeAssignment(assignment));
                    });
                  });
                }
          }
        });
      }

      return assignments;
    } catch (error) {
      console.error('Error fetching module tests:', error);
      return [];
    }
  },

  /**
   * Fetch tests for a specific lesson (Master Tests)
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @param {string} lessonId - Lesson ID
   * @returns {Promise<Array>} Array of test assignments
   */
  fetchLessonTests: async (userId, courseId, lessonId) => {
    try {
      const response = await api.getStudentAssignmentsDirect(userId, { 
        exam_type: 'Master Test', 
        course_id: courseId,
        lesson_id: lessonId
      });

      // Flatten and filter assignments for this lesson
      const assignments = [];
      if (response.data.lessons) {
        response.data.lessons.forEach(lesson => {
          if (lesson.course_id === parseInt(courseId) && lesson.lesson_id === parseInt(lessonId)) {
                if (lesson.assignments_by_level) {
                  Object.values(lesson.assignments_by_level).forEach(levelAssignments => {
                    levelAssignments.forEach(assignment => {
                      assignments.push(normalizeAssignment(assignment));
                    });
                  });
                }
          }
        });
      }

      return assignments;
    } catch (error) {
      console.error('Error fetching lesson tests:', error);
      return [];
    }
  },

  /**
   * Fetch tests for a specific topic (Basic Exams)
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @param {string} lessonId - Lesson ID (Basic Exams are lesson-level)
   * @param {string} topicId - Topic ID to filter assignments
   * @returns {Promise<Array>} Array of test assignments
   */
  fetchTopicTests: async (userId, courseId, lessonId, topicId) => {
    try {
      const response = await api.getStudentAssignmentsDirect(userId, { 
        exam_type: 'Basic Exam', 
        course_id: courseId,
        lesson_id: lessonId,
        topic_id: topicId
      });

      console.log('fetchTopicTests API response for topicId:', topicId, 'lessonId:', lessonId, response.data);

      // Flatten and filter assignments for this topic
      const assignments = [];
      if (response.data.lessons) {
        response.data.lessons.forEach(lesson => {
          if (lesson.course_id === parseInt(courseId) && lesson.lesson_id === parseInt(lessonId)) {
                if (lesson.assignments_by_level) {
                  Object.values(lesson.assignments_by_level).forEach(levelAssignments => {
                    levelAssignments.forEach(assignment => {
                      // TODO: Filter by topic_id when backend supports it
                      // For now, we'll include all Basic Exams for the lesson
                      // and let the frontend handle the filtering
                      console.log('Assignment found:', assignment.id, assignment.title, 'topic_id:', assignment.topic_id);
                      assignments.push(normalizeAssignment(assignment));
                    });
                  });
                }
          }
        });
      }

      console.log('Filtered assignments for topic:', topicId, assignments);
      return assignments;
    } catch (error) {
      console.error('Error fetching topic tests:', error);
      return [];
    }
  },

  /**
   * Get current user ID from API
   * @returns {Promise<string|null>} User ID or null if not found
   */
  getCurrentUserId: async () => {
    try {
      const userResponse = await api.getMe();
      return userResponse.data.id || userResponse.data.user_id;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  /**
   * Create test click handler for TestInstructions modal
   * @param {Object} test - Test object
   * @param {string} courseId - Course ID
   * @param {Function} setSelectedTest - State setter for selected test
   * @param {Function} setShowTestInstructions - State setter for modal visibility
   */
  createTestClickHandler: (test, courseId, setSelectedTest, setShowTestInstructions) => {
    return () => {
      setSelectedTest({
        ...test,
        fromCourse: true,
        course_id: courseId
      });
      setShowTestInstructions(true);
    };
  },

  /**
   * Fetch tests for a specific content (Content Tests)
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @param {string} contentId - Content ID
   * @returns {Promise<Array>} Array of test assignments
   */
  fetchContentTests: async (userId, courseId, contentId) => {
    try {
      console.log('🔍 fetchContentTests called with:', { userId, courseId, contentId });
      
      const response = await api.getStudentAssignmentsDirect(userId, { 
        exam_type: 'Content Test', 
        course_id: courseId,
        content_id: contentId
      });

      console.log('📥 fetchContentTests API response:', response.data);

      // Flatten and filter assignments for this content
      const assignments = [];
      if (response.data.lessons) {
        response.data.lessons.forEach(lesson => {
          if (lesson.course_id === parseInt(courseId)) {
                if (lesson.assignments_by_level) {
                  Object.values(lesson.assignments_by_level).forEach(levelAssignments => {
                    levelAssignments.forEach(assignment => {
                      // Filter assignments that match the content_id
                      if (assignment.content_id === parseInt(contentId)) {
                        assignments.push(normalizeAssignment(assignment));
                      }
                    });
                  });
                }
          }
        });
      }

      console.log('✅ fetchContentTests filtered assignments:', assignments);
      return assignments;
    } catch (error) {
      console.error('❌ Error fetching content tests:', error);
      return [];
    }
  },

  /**
   * Create close handler for TestInstructions modal
   * @param {Function} setSelectedTest - State setter for selected test
   * @param {Function} setShowTestInstructions - State setter for modal visibility
   */
  createCloseHandler: (setSelectedTest, setShowTestInstructions) => {
    return () => {
      setShowTestInstructions(false);
      setSelectedTest(null);
    };
  }
};

export default testService;
